/**
 * #1316 review round 2 — THE re-ask of a load whose answer a session end dropped (`errorText` → `null`,
 * `isSessionEndedError`), for a view that outlives the end.
 *
 * The re-ask runs after the render that follows the end (a state nonce), so it goes out as the session now here. On
 * the share view that is not yet true after every end: at an end that MOVES the share session (an identity switch, a
 * replaced session — `endMovesShareSession`) the view keeps reading the ended sid until the new session's `/info` or
 * sub-session lands. A re-ask then would go out AS THE ENDED share session (a second editor load, the old tree). So:
 *  - the load records the share session it went out as (`begin()`, at its start);
 *  - at the drop it re-asks at once when that sid is not a share sid (the drive), the view already reads another sid,
 *    or the end kept the share session (the sid is still the current one);
 *  - otherwise it waits: it re-asks on the first render where the view reads a DIFFERENT sid — unless a load began in
 *    that render's effects (a view the move rebuilds through its own deps, `identityKey`, loads afresh anyway). A view
 *    the move remounts (`key={sid}`) loads afresh too; the wait then simply ends with it.
 *
 * `readsAs`: what names the share session this view reads — the explorer's `sessionId` prop, a probe's serve URL;
 * omitted = the share view's active sid, the one api-client's `X-Share-Session` header carries
 * (`getShareSessionToken`) — `null` off the share view.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { getShareSessionToken } from '@papan/api-client'
import { lastEndMovedShareSession } from './sessionEnded'

/** The share view's active sid; `null` off it — and with a test's api-client mock that leaves the getter out. */
function activeShareSid(): string | null {
  try { return getShareSessionToken() } catch { return null }
}

export interface DroppedLoad {
  /** The load's answer was dropped by a session end: ask again, as the session now here (never as the ended one). */
  dropped(): void
}

export function useDroppedLoadReask(readsAs?: string | null): { reask: number; begin: () => DroppedLoad } {
  const [reask, setReask] = useState(0)
  const readsAsRef = useRef(readsAs)
  readsAsRef.current = readsAs
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])
  const current = useCallback((): string | null => (readsAsRef.current !== undefined ? readsAsRef.current ?? null : activeShareSid()), [])
  /** The ended share session a dropped load waits to see replaced; `null` = none waiting. */
  const waitingFor = useRef<string | null>(null)

  /** Loads begun so far — a load begun after the wait ended (in the same commit) makes its re-ask redundant. */
  const begun = useRef(0)

  const begin = useCallback((): DroppedLoad => {
    begun.current += 1
    const sentAs = current()
    return {
      dropped() {
        if (!mountedRef.current) return
        if (!sentAs || current() !== sentAs || !lastEndMovedShareSession()) { waitingFor.current = null; setReask((n) => n + 1); return }
        waitingFor.current = sentAs
      },
    }
  }, [current])

  // no deps: checked after every render — the one where the view reads the new share session re-asks. Review round 2
  // (H2b, M1): after that commit's effects — a consumer whose own deps changed with the sid (the explorer's root
  // listing on `identityKey`) already loaded as the new session in it; a re-ask then would cancel that load and send
  // it again, so it is skipped when a load began since.
  useEffect(() => {
    const waiting = waitingFor.current
    if (waiting === null || current() === waiting) return
    waitingFor.current = null
    const at = begun.current
    queueMicrotask(() => {
      if (!mountedRef.current || begun.current !== at) return
      setReask((n) => n + 1)
    })
  })

  return { reask, begin }
}
